/**
 * Admin V3 — Financial / Withdrawals — Table component
 *
 * 8 columns: (expand) | Date / Heure | Statut | Chauffeur | Montant
 *           | Identifiant Stripe (truncated) | Référence | Actions.
 *
 * Per-row expandable detail:
 *   - Full withdrawal_id
 *   - Full identifiant Stripe (when present)
 *   - Idempotency key
 *   - Raw error message (when status = failed)
 *   - created_at, updated_at
 *
 * Failed AND reversed rows: rose left-border.
 *
 * Chauffeur cell links to /admin-v3/drivers/:id when driver_id present.
 *
 * Stripe ID truncation: prefix(6) + … + suffix(4) when length > 12.
 * Format example: tr_1Ax2…GHIJ
 *
 * Driver Stripe ACCOUNT id (stripe_account_id) is NOT consumed even if
 * present in payload — only Stripe TRANSFER id (proof-of-execution) is
 * surfaced, per Phase 2.14 ruling.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, AlertCircle, RotateCcw, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  currency,
  EmptyState,
  SmallPill,
  withdrawalStatusMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatTs = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

// Truncate long external IDs to "prefix…suffix" for in-row display.
const truncateMiddle = (s, prefixLen = 6, suffixLen = 4) => {
  if (!s) return '';
  if (s.length <= prefixLen + suffixLen + 1) return s;
  return `${s.slice(0, prefixLen)}…${s.slice(-suffixLen)}`;
};

const truncateEnd = (s, n = 12) => {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
};

const initialsOf = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(8).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const DetailPanel = ({ withdrawal, locale }) => {
  const rows = [
    withdrawal.id                  && [t('financial.withdrawals.detail.withdrawalId', locale), withdrawal.id],
    withdrawal.stripe_transfer_id  && [t('financial.withdrawals.detail.stripeId',     locale), withdrawal.stripe_transfer_id],
    withdrawal.idempotency_key     && [t('financial.withdrawals.detail.idempotency',  locale), withdrawal.idempotency_key],
    withdrawal.status === 'failed' && withdrawal.error_message
      && [t('financial.withdrawals.detail.errorMessage', locale), withdrawal.error_message],
    withdrawal.created_at          && [t('financial.withdrawals.detail.createdAt',    locale), formatTs(withdrawal.created_at, locale)],
    withdrawal.updated_at          && [t('financial.withdrawals.detail.updatedAt',    locale), formatTs(withdrawal.updated_at, locale)],
    withdrawal.tenant_id           && [t('financial.withdrawals.detail.tenant',       locale), withdrawal.tenant_id],
  ].filter(Boolean);

  if (rows.length === 0) {
    return <div className="text-[11px] italic text-slate-400 px-2 py-1">—</div>;
  }

  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
            <dd className="text-slate-700 font-mono break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const FinancialWithdrawalsTable = ({ withdrawals, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-financial-withdrawals-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('financial.withdrawals.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline" size="sm" onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-financial-withdrawals-error-action"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('financial.withdrawals.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={Wallet}
      title={t('financial.withdrawals.empty.title', locale)}
      desc={t(isFilterActive ? 'financial.withdrawals.empty.descFilter' : 'financial.withdrawals.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('financial.withdrawals.col.datetime',  locale),
                t('financial.withdrawals.col.status',    locale),
                t('financial.withdrawals.col.driver',    locale),
                t('financial.withdrawals.col.amount',    locale),
                t('financial.withdrawals.col.stripeId',  locale),
                t('financial.withdrawals.col.reference', locale),
                t('financial.withdrawals.col.actions',   locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : withdrawals.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              withdrawals.map((w) => {
                const key = w.id || `${w.created_at}-${w.driver_id}-${w.amount}`;
                const isExpanded = Boolean(expanded[key]);
                const statusKey = w.status || 'pending';
                const statusMeta = withdrawalStatusMeta[statusKey] || {
                  label: statusKey,
                  pill: 'bg-slate-100 text-slate-500 border border-slate-200',
                };
                const isAlertRow = statusKey === 'failed' || statusKey === 'reversed';
                const driverPath = w.driver_id ? `/admin/drivers/${w.driver_id}` : null;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-b border-slate-100 hover:bg-slate-50/70 transition-colors',
                        isAlertRow && 'bg-rose-50/30'
                      )}
                      style={isAlertRow ? { boxShadow: 'inset 3px 0 0 #ef4444' } : undefined}
                    >
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded ? t('financial.withdrawals.detail.hide', locale) : t('financial.withdrawals.detail.show', locale)}
                          data-testid={`adminv3-financial-withdrawals-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Date / Heure */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatTs(w.created_at, locale)}
                      </td>

                      {/* Statut */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={statusMeta} />
                      </td>

                      {/* Chauffeur */}
                      <td className="px-4 py-3.5">
                        {driverPath ? (
                          <Link
                            to={driverPath}
                            className="flex items-center gap-2.5 group"
                            data-testid={`adminv3-financial-withdrawals-driver-${key}`}
                          >
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-indigo-300 flex-shrink-0">
                              {initialsOf(w.driver_name)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-[13px] font-medium text-slate-900 truncate group-hover:text-indigo-700">{w.driver_name || '—'}</div>
                              <div className="text-[11px] text-slate-400 truncate">{w.driver_email || ''}</div>
                            </div>
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Montant */}
                      <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap tabular-nums">
                        {currency(w.amount ?? 0)}
                      </td>

                      {/* Identifiant Stripe (truncated) */}
                      <td className="px-4 py-3.5 font-mono text-[11px] text-slate-500">
                        {w.stripe_transfer_id ? truncateMiddle(w.stripe_transfer_id, 6, 4) : '—'}
                      </td>

                      {/* Référence (withdrawal id truncated) */}
                      <td className="px-4 py-3.5 font-mono text-[12px] text-slate-500">
                        {truncateEnd(w.id, 12) || '—'}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3.5">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={t('financial.withdrawals.action.open', locale)}
                          data-testid={`adminv3-financial-withdrawals-open-${key}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={8} className="px-4 py-2">
                          <DetailPanel withdrawal={w} locale={locale} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default FinancialWithdrawalsTable;
